<script setup lang="ts">
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator
} from '@/components/ui/command';
import { cn, normalizeString } from '@/utils';
import type { Address, AddressData } from '~~/core/entities/Addresses';
import type { SearchSuggestProps } from '.';
import Group from './Group.vue';

import { useAddresses } from '~/composables/useAddresses';
import type { PropertyFilters } from '~/core/entities/property/filters';
import MacroregionsGroupIcon from '~icons/material-symbols-light/explore-outline';
import RegionsGroupIcon from '~icons/material-symbols-light/home-work-outline';
import CitiesGroupIcon from '~icons/material-symbols-light/map-outline';
import { slugify } from '~~/utils';

const props = withDefaults(defineProps<SearchSuggestProps>(), {
	inputPlaceholder: 'Busque por bairros, cidades e códigos em São Paulo',
	isFetchingPropertyByCodeSearch: false
});

const emit = defineEmits<{
	(
		e: 'select',
		value: {
			regions: PropertyFilters['regions'];
			cities: PropertyFilters['cities'];
			macroregions: PropertyFilters['macroregions'];
			code: string | null;
			searchTerm: string;
		}
	): void;
	(e: 'update:search-term', value: string): void;
	(e: 'emptyResultOnFilter', searchTerm: string): void;
}>();

const regions = defineModel<PropertyFilters['regions']>('regions');
const cities = defineModel<PropertyFilters['cities']>('cities');
const macroregions =
	defineModel<PropertyFilters['macroregions']>('macroregions');

const lastResearchs = useCookie<
	{ group: string; groupData: Address | AddressData }[]
>('search-suggest', {
	default: () => [],
	sameSite: 'lax'
});
const modelValue = ref<{
	group: string;
	groupData: Address | AddressData | string;
}>();

const searchTerm = ref<string>('');
const inputedSearchTerm = ref<string>('');
const addressesSearchTerm = computed<string>(() => {
	const clearedByStateSearchTerm = searchTerm.value
		.replace(new RegExp(`- ${state.value}`, 'gi'), '')
		.trim();

	if (modelValue.value && typeof modelValue.value.groupData !== 'string') {
		const hasSelectedMacroregionOrCity =
			modelValue.value.group === 'macroregion' ||
			modelValue.value.group === 'city';
		const searchTermIsInSelectedGroupLabel =
			modelValue.value.groupData.label.includes(clearedByStateSearchTerm);

		const hasSelectedRegion = modelValue.value.group === 'region';

		if (hasSelectedMacroregionOrCity && searchTermIsInSelectedGroupLabel)
			return '';

		const searchTermIsInSelectedGroupMacroregion =
			'data' in modelValue.value.groupData &&
			modelValue.value.groupData.data.macroregion.includes(
				clearedByStateSearchTerm
			);

		if (hasSelectedRegion && searchTermIsInSelectedGroupMacroregion) return '';
	}

	return normalizeString(clearedByStateSearchTerm);
});

const {
	addresses,
	state,
	macroregions: filteredMacroregions,
	filteredRegions,
	filteredCities
} = useAddresses(addressesSearchTerm);

watch(addressesSearchTerm, () => {
	emit('update:search-term', addressesSearchTerm.value);
});

const commandEl = ref<HTMLElement | null>(null);
const isDropdownOpened = shallowRef<boolean>(false);

const hasMinSearchTerm = computed<boolean>(
	() => addressesSearchTerm.value.length >= 3
);

const filteredLastResearchs = computed<
	{ group: string; groupData: Address | AddressData }[]
>(() =>
	hasMinSearchTerm.value ? lastResearchs.value : lastResearchs.value.slice(-3)
);

const hasFilteredResults = computed<boolean>(() => {
	return !!(filteredRegions.value.length || filteredCities.value.length);
});

const shouldShowEmptyState = computed<boolean>(() => {
	return (
		!hasFilteredResults.value &&
		!searchByCodeOption.value.length &&
		searchTerm.value.length >= 3
	);
});

const shouldShowLastResearchs = computed<boolean>(() => {
	// Aparece sempre que tiver ultimas buscas e a busca for menor que 3 caracteres
	const hasLastResearchs = lastResearchs.value.length > 0;

	return (
		!hasFilteredResults.value && hasLastResearchs && !hasMinSearchTerm.value
	);
});

const searchByCodeOption = computed(() => {
	const regex = /^[a-zA-Z0-9]{2,5}(\d{1,8})?$/m;

	const isValidCode = regex.test(searchTerm.value);

	return isValidCode && searchTerm.value.length >= 3
		? [
				{
					group: 'code',
					groupData: searchTerm.value.toUpperCase()
				}
			]
		: [];
});

function clear() {
	searchTerm.value = '';
	macroregions.value = [];
}

function closeDropdown() {
	isDropdownOpened.value = false;
}

function setSearchTermToModelValue() {
	if (modelValue.value) {
		inputedSearchTerm.value = searchTerm.value;
		searchTerm.value = getDisplayValue(modelValue.value);
	}
}

function handleInputFocus() {
	searchTerm.value = '';
	isDropdownOpened.value = true;
}

onKeyStroke('Escape', e => {
	e.preventDefault();
	closeDropdown();
	setSearchTermToModelValue();
});

onClickOutside(commandEl, () => {
	closeDropdown();
	setSearchTermToModelValue();
});

async function updateRegionsModel(selectedGroup: {
	group: string;
	groupData: Address | AddressData;
}) {
	if (selectedGroup.group !== 'region') {
		regions.value = [];
		return;
	}

	const isAlreadySelected = regions.value?.includes(
		selectedGroup.groupData.label
	);

	if (isAlreadySelected) return;

	regions.value =
		selectedGroup.group === 'region' ? [selectedGroup.groupData.label] : [];

	macroregions.value =
		selectedGroup.group === 'region' && 'data' in selectedGroup.groupData
			? [selectedGroup.groupData.data.macroregion]
			: [];
}

async function updateCitiesModel(selectedGroup: {
	group: string;
	groupData: Address | AddressData;
}) {
	if (selectedGroup.group === 'macroregion') {
		cities.value = [];
		return;
	}

	const newCity =
		selectedGroup.group === 'region' && 'data' in selectedGroup.groupData
			? selectedGroup.groupData.data.city
			: selectedGroup.groupData.label;

	const isAlreadySelected = cities.value?.includes(newCity);

	if (isAlreadySelected) return;

	cities.value = [newCity];
	macroregions.value = [];
}

async function updateMacroregionsModel(selectedGroup: {
	group: string;
	groupData: Address | AddressData;
}) {
	if (selectedGroup.group !== 'macroregion') return;

	const newMacroregion = selectedGroup.groupData.label;
	const slugfiedMacroregion = slugify(newMacroregion);

	const isAlreadySelected =
		macroregions.value?.includes(newMacroregion) ||
		props.locationSlug?.includes(slugfiedMacroregion) ||
		false;

	if (isAlreadySelected) return;

	macroregions.value = [newMacroregion];
}

async function updateModelValue(
	event: string | number | boolean | Record<string, any>
) {
	const value = event as {
		group: string;
		groupData: Address | AddressData | string;
	};

	const hasSelectedAddress = value.group !== 'code';

	modelValue.value = value;

	if (hasSelectedAddress && typeof value.groupData !== 'string') {
		const newValue = value as {
			group: string;
			groupData: Address | AddressData;
		};

		updateResearchs(newValue);

		await updateMacroregionsModel(newValue);
		await updateCitiesModel(newValue);
		await updateRegionsModel(newValue);

		closeDropdown();
	}

	const code = value.group === 'code' ? (value.groupData as string) : null;

	emit('select', {
		regions: regions.value,
		cities: cities.value,
		macroregions: macroregions.value,
		code,
		searchTerm: inputedSearchTerm.value
	});
}

watch(
	() => props.isFetchingPropertyByCodeSearch,
	isFetching => {
		if (!isFetching) closeDropdown();
	}
);

function getDisplayValue(
	value: string | number | boolean | Record<string, any>
): string {
	const { group, groupData } = value as {
		group: string;
		groupData: Address | AddressData | string;
	};

	if (typeof groupData === 'string') {
		return groupData as string;
	}

	if (group === 'region' && 'data' in groupData) {
		const cityOrMacroregion = groupData.data.city || groupData.data.macroregion;

		return `${cityOrMacroregion} - ${state.value}`;
	}

	return `${groupData.label} - ${state.value}`;
}

function updateInputedSearchTerm(value: string) {
	inputedSearchTerm.value = value;
}

function updateResearchs(newResearch: {
	group: string;
	groupData: Address | AddressData;
}) {
	if (lastResearchs.value?.length >= 3) {
		lastResearchs.value.pop();
	}

	lastResearchs.value = [
		...new Set([newResearch, ...(lastResearchs.value || [])])
	];
}

function removeResearchs(search: {
	group: string;
	groupData: Address | AddressData;
}) {
	lastResearchs.value = lastResearchs.value.filter(
		lastSearch => lastSearch !== search
	);
}

function updateModelValueFromModels(
	group: string,
	data?: Address | AddressData
) {
	if (!data) return;

	modelValue.value = {
		group,
		groupData: data
	};
}

watch(
	() => [regions.value, cities.value, macroregions.value],
	([newRegions, newCities, newMacroregions]) => {
		if (newRegions && newRegions.length) {
			const region = addresses.value[0]?.regions.find(
				region => region.label === newRegions[0]
			);

			updateModelValueFromModels('region', region);
			return;
		}

		if (newCities && newCities.length) {
			const city = addresses.value[0]?.cities.find(
				city => city.label === newCities[0]
			);

			updateModelValueFromModels('city', city);
			return;
		}

		if (newMacroregions && newMacroregions.length) {
			const macroregion = addresses.value[0]?.macroregions.find(
				macroregion => macroregion.label === newMacroregions[0]
			);

			updateModelValueFromModels('macroregion', macroregion);
			return;
		}
	},
	{
		immediate: true
	}
);

watch(hasFilteredResults, value => {
	if (!value) emit('emptyResultOnFilter', searchTerm.value);
});
</script>

<template>
	<div ref="commandEl">
		<Command
			v-model="modelValue"
			v-model:search-term="searchTerm"
			:open="isDropdownOpened"
			:display-value="getDisplayValue"
			:class="cn('h-auto w-full border', props.class)"
			@update:model-value="updateModelValue"
		>
			<CommandInput
				:placeholder="inputPlaceholder"
				:wrapper-classes="inputWrapperClasses"
				:class="cn('h-[62px] text-ellipsis pl-5 text-primary', inputClasses)"
				:auto-focus="false"
				@update:model-value="updateInputedSearchTerm"
				@focus.prevent="handleInputFocus"
			>
				<template #prefix>
					<slot name="input-prefix" />
				</template>
				<template #suffix>
					<div
						v-show="modelValue"
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							:class="
								cn(
									'size-5 cursor-pointer text-white-dark-3',
									inputClearIconClasses
								)
							"
							@click="clear"
						/>
					</div>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="cn('size-7 text-primary', inputSearchIconClasses)"
						/>
					</div>
				</template>
			</CommandInput>

			<CommandList
				:class="
					cn(
						'-mt-2 h-full border border-white-dark-2 bg-white p-0',
						props.listWrapperClasses
					)
				"
			>
				<div
					v-show="shouldShowEmptyState"
					:class="cn('px-5 py-6', emptyStateWrapperClasses)"
				>
					<ListingEmptyState
						:show-badges="false"
						:icon-wrapper-classes="
							cn('size-[47.5px]', emptyStateIconWrapperClasses)
						"
						:icon-classes="cn('size-[22.5px]', emptyStateIconClasses)"
						:title-classes="cn('text-lg', emptyStateTitleClasses)"
						:description-classes="cn('text-sm', emptyStateDescriptionClasses)"
					>
						<template #title>Nenhum resultado encontrado</template>
						<template #description>
							Tente uma busca diferente ou verifique a grafia
						</template>
					</ListingEmptyState>
				</div>

				<CommandSeparator
					v-if="shouldShowLastResearchs && hasMinSearchTerm"
					class="bg-white-dark-2"
				/>

				<CommandGroup
					v-if="shouldShowLastResearchs && hasMinSearchTerm"
					class="p-0"
				>
					<template #heading>
						<div
							:class="
								cn(
									'flex h-12 cursor-default items-center gap-3 px-4 font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] text-primary',
									groupHeadingClasses
								)
							"
						>
							<IconMaterialSymbolsLightHistory
								:class="cn('size-5', groupIconClasses)"
							/>
							Últimas buscas
						</div>
					</template>

					<CommandItem
						v-for="search in filteredLastResearchs"
						:key="`search-suggest-last-researchs-${search}`"
						:value="search"
						:class="
							cn(
								'flex items-center justify-between py-2 pl-12 pr-4 data-[highlighted]:bg-white-dark-2',
								groupItemWrapperClasses
							)
						"
					>
						<p
							v-if="search.group === 'region' && 'data' in search.groupData"
							:class="
								cn(
									'flex flex-col text-start text-base font-normal leading-normal text-primary',
									groupItemTextClasses
								)
							"
						>
							{{ search.groupData.label }}
							<small
								:class="
									cn(
										'text-start text-xs font-normal leading-normal text-primary-light-2',
										groupItemSubTextClasses
									)
								"
							>
								{{ search.groupData.data.city }} - {{ state }}
							</small>
						</p>

						<p
							v-if="search.group === 'city' || search.group === 'macroregion'"
							:class="
								cn(
									'flex flex-col text-start text-base font-normal leading-normal text-primary',
									groupItemTextClasses
								)
							"
						>
							{{ search.groupData.label }}
							<small
								:class="
									cn(
										'text-start text-xs font-normal leading-normal text-primary-light-2',
										groupItemSubTextClasses
									)
								"
							>
								{{ state }}
							</small>
						</p>

						<IconMaterialSymbolsLightClose
							:class="
								cn(
									'size-5 cursor-pointer text-primary-light-1',
									lastResearchsIconClasses
								)
							"
							@click.stop="removeResearchs(search)"
						/>
					</CommandItem>
				</CommandGroup>

				<Group
					v-show="searchTerm.length < 3"
					:heading="{ text: 'Regiões', icon: MacroregionsGroupIcon }"
					group-key="macroregion"
					:state="state"
					:group-items="filteredMacroregions"
					:search-term="searchTerm"
					:class="groupWrapperClasses"
					:group-heading-classes="groupHeadingClasses"
					:group-icon-classes="groupIconClasses"
					:group-item-wrapper-classes="groupItemWrapperClasses"
					:group-item-text-classes="groupItemTextClasses"
					:group-item-sub-text-classes="groupItemSubTextClasses"
				/>

				<CommandSeparator
					v-if="!hasMinSearchTerm && hasMinSearchTerm"
					class="bg-white-dark-2"
				/>

				<Group
					v-if="hasMinSearchTerm"
					:heading="{ text: 'Cidades', icon: CitiesGroupIcon }"
					group-key="city"
					:state="state"
					:group-items="filteredCities"
					:search-term="searchTerm"
					:class="groupWrapperClasses"
					:group-heading-classes="groupHeadingClasses"
					:group-icon-classes="groupIconClasses"
					:group-item-wrapper-classes="groupItemWrapperClasses"
					:group-item-text-classes="groupItemTextClasses"
					:group-item-sub-text-classes="groupItemSubTextClasses"
				/>

				<CommandSeparator
					v-if="filteredCities.length && hasMinSearchTerm"
					class="bg-white-dark-2"
				/>

				<Group
					v-if="hasMinSearchTerm"
					:heading="{ text: 'Bairros', icon: RegionsGroupIcon }"
					group-key="region"
					:state="state"
					:group-items="filteredRegions.slice(0, 51)"
					:search-term="searchTerm"
					:class="groupWrapperClasses"
					:group-heading-classes="groupHeadingClasses"
					:group-icon-classes="groupIconClasses"
					:group-item-wrapper-classes="groupItemWrapperClasses"
					:group-item-text-classes="groupItemTextClasses"
					:group-item-sub-text-classes="groupItemSubTextClasses"
				/>

				<CommandSeparator class="bg-white-dark-2" />

				<CommandGroup class="p-0">
					<template #heading>
						<div
							:class="
								cn(
									'flex h-12 cursor-default items-center gap-3 px-4 font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px] text-primary',
									groupHeadingClasses
								)
							"
						>
							<IconMaterialSymbolsLightTag
								:class="cn('size-5', groupIconClasses)"
							/>
							Código de imóvel
						</div>
					</template>

					<CommandItem
						v-for="option in searchByCodeOption"
						:key="`search-suggest-code-option-${option.groupData}`"
						:value="option"
						:class="
							cn(
								'flex cursor-pointer items-center justify-between py-4 pl-12 pr-4 data-[highlighted]:bg-white-dark-2',
								groupItemWrapperClasses
							)
						"
					>
						<p
							:class="
								cn(
									'flex flex-col text-start text-base font-normal leading-normal text-primary',
									groupItemTextClasses
								)
							"
						>
							Buscar "{{ option.groupData }}" como possível imóvel
						</p>

						<IconPilarCircularProgress
							v-show="isFetchingPropertyByCodeSearch"
							class="size-5 animate-spin text-white-dark-4"
						/>
					</CommandItem>
				</CommandGroup>
			</CommandList>
		</Command>
	</div>
</template>
